import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';
import { Carousel } from 'dibs-carousel';
import { Swiper } from 'dibs-pinch-zoom-swipe';
import { FormattedMessage } from 'react-intl';

import { HpSharedModuleContainer } from '../HpSharedModuleContainer/HpSharedModuleContainer';
import { SharedCarouselDot } from '../../sharedComponents/SharedCarouselDot/SharedCarouselDot';
import { HpRespHeroModuleItem } from './HpRespHeroModuleItem';

import { RenderIn, device } from '../../utils/RenderIn';
import { HpSharedCarouselTracking } from '../HpSharedCarouselTracking/HpSharedCarouselTracking';
import { trackHeroImpression, trackHeroItemClick } from './helpers/HeroTracking';
import { HpSharedInViewport } from '../HpSharedInViewport/HpSharedInViewport';
/**
 *  Removes items residue because we want to make sure that all of the carousel pages are full
 */
function removeItemsResidue({ totalItems, itemsPerPage }) {
    const residue = totalItems % itemsPerPage;

    if (residue !== 0) {
        return totalItems - residue;
    }

    return totalItems;
}

function HpRespHeroModuleComponent({ homepage }) {
    if (!homepage.heroModule) {
        return null;
    }

    const { items, title } = homepage.heroModule;

    return (
        <HpSharedModuleContainer>
            <HpSharedModuleContainer.Header>
                {title ? (
                    title
                ) : (
                    <FormattedMessage
                        id="homepage.modules.hero.title"
                        defaultMessage="Editor's Picks"
                    />
                )}
            </HpSharedModuleContainer.Header>
            <RenderIn deviceTypes={[device.DESKTOP, device.TABLET]}>
                {({ currentDevice }) => {
                    const itemsPerPage = currentDevice === device.DESKTOP ? 4 : 3;

                    return (
                        <HpSharedCarouselTracking
                            items={items}
                            itemsPerPage={itemsPerPage}
                            onItemsImpression={({ visibleItems, index }) =>
                                trackHeroImpression({
                                    items: visibleItems,
                                    startIndex: index,
                                })
                            }
                        >
                            {({ handleIndexChange }) => (
                                <HpSharedInViewport>
                                    {({ inViewport }) => (
                                        <Carousel
                                            isAutoRun={inViewport}
                                            dataTn="hero-items-carousel"
                                            showDots
                                            hideArrows
                                            totalItems={removeItemsResidue({
                                                totalItems: items.length,
                                                itemsPerPage,
                                            })}
                                            itemsPerPage={itemsPerPage}
                                            step={itemsPerPage}
                                            renderItem={({ index, isVisible }) => (
                                                <HpRespHeroModuleItem
                                                    item={items[index]}
                                                    isVisible={isVisible}
                                                    onClick={() =>
                                                        trackHeroItemClick({
                                                            item: items[index],
                                                            index,
                                                        })
                                                    }
                                                />
                                            )}
                                            onIndexChange={handleIndexChange}
                                            renderDot={({ isCurrentDot }) => (
                                                <SharedCarouselDot isActive={isCurrentDot} />
                                            )}
                                        />
                                    )}
                                </HpSharedInViewport>
                            )}
                        </HpSharedCarouselTracking>
                    );
                }}
            </RenderIn>

            <RenderIn deviceTypes={[device.MOBILE]}>
                {() => {
                    const itemsPerPage = 1.5;

                    return (
                        <HpSharedCarouselTracking
                            items={items}
                            itemsPerPage={itemsPerPage}
                            onItemsImpression={({ visibleItems, index }) =>
                                trackHeroImpression({
                                    items: visibleItems,
                                    startIndex: index,
                                })
                            }
                        >
                            {({ handlePageChange }) => (
                                <Swiper itemsPerPage={itemsPerPage} onPageChange={handlePageChange}>
                                    {items.map((item, index) => (
                                        <HpRespHeroModuleItem
                                            key={item.cmsId}
                                            item={item}
                                            onClick={() => trackHeroItemClick({ item, index })}
                                        />
                                    ))}
                                </Swiper>
                            )}
                        </HpSharedCarouselTracking>
                    );
                }}
            </RenderIn>
        </HpSharedModuleContainer>
    );
}

HpRespHeroModuleComponent.propTypes = {
    homepage: PropTypes.object.isRequired,
};

export const HpRespHeroModule = createFragmentContainer(HpRespHeroModuleComponent, {
    homepage: graphql`
        fragment HpRespHeroModule_homepage on Homepage {
            heroModule: module(type: "hero") {
                ... on HeroModule {
                    title
                    items {
                        cmsId
                        link
                        imageUrl
                        title
                        contemporaryTrackingString
                    }
                }
            }
        }
    `,
});
